<script setup lang="ts">
import {
  DialogRoot,
  DialogTrigger,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogClose,
  DialogTitle,
  DialogDescription,
} from "reka-ui";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";

import { caseService } from "@/services";
import { isApiError } from "@/services/Api";

import { useBrandStore } from "@/stores/brand-store";

import {
  CASE_REFERENCE_LENGTH_MAX,
  CASE_REFERENCE_LENGTH_MIN,
  getCaseReferenceValidationMessage,
  validateCaseReference,
} from "@/utils/case-reference-validation";

import type { CaseReferenceValidationError } from "@/utils/case-reference-validation";

const CASE_REFERENCE_LENGTH_THRESHOLD = 20;

const router = useRouter();

const isLoading = ref(false);
const state = ref<"EXISTS" | "NOT_EXISTS" | "FORBIDDEN">();
const errorReason = ref<CaseReferenceValidationError>();

const lastSearchCaseName = ref("");
const currentCaseName = ref("");

const brand = computed(() => useBrandStore().selectedBrand);
const charsRemaining = computed(() => CASE_REFERENCE_LENGTH_MAX - currentCaseName.value.length);
const quarantineEnabled = computed(() => useBrandStore().featuresConfig?.quarantine !== "disabled");

async function createCase() {
  const { selectedBrand, selectedClient } = useBrandStore();

  if (!currentCaseName.value || selectedClient == null || selectedBrand == null) return;

  errorReason.value = validateCaseReference(currentCaseName.value);

  if (errorReason.value != null) return;

  try {
    lastSearchCaseName.value = currentCaseName.value;
    isLoading.value = true;
    await caseService.GetCaseMetadata(selectedClient, selectedBrand, lastSearchCaseName.value);

    // If here, the request returned of 200 and the case exists
    state.value = "EXISTS";
  } catch (error) {
    if (isApiError(error) && error.status === 403) state.value = "FORBIDDEN";
    else {
      state.value = "NOT_EXISTS";
      router.push({ name: "upload", params: { brand: brand.value, casename: lastSearchCaseName.value } });
    }
  } finally {
    isLoading.value = false;
  }
}
</script>
<template>
  <DialogRoot>
    <DialogTrigger v-if="quarantineEnabled" v-can:upload class="tw-btn-md-green tw-min-w-20">
      Create Case
    </DialogTrigger>
    <DialogPortal>
      <DialogOverlay class="tw-fixed tw-inset-0 tw-z-[1000000] tw-h-full tw-w-full tw-bg-black/30" />
      <DialogContent class="tw-fixed tw-inset-0 tw-z-[1000000] tw-flex tw-items-center tw-justify-center">
        <form
          class="tw-w-full tw-max-w-md tw-space-y-2 tw-rounded-lg tw-border tw-bg-white tw-px-4 tw-py-4 tw-shadow-lg"
          @submit.prevent="createCase"
        >
          <DialogTitle class="tw-text-center tw-text-lg tw-font-semibold">Create Case</DialogTitle>
          <DialogDescription
            v-if="state == null"
            class="tw-sr-only tw-flex tw-select-none tw-items-center tw-gap-2 tw-rounded-md tw-border tw-p-2"
          >
            Type the desired case reference in the text box and submit the form.
          </DialogDescription>
          <DialogDescription v-else-if="state === 'EXISTS'" :as-child="true">
            <a
              :href="`/${brand}/${lastSearchCaseName}`"
              class="tw-flex tw-select-none tw-items-center tw-gap-2 tw-rounded-md tw-border tw-bg-blue-200/45 tw-p-2 tw-text-blue-900"
            >
              <i class="fas fa fa-circle-info"></i>
              <span class="tw-underline tw-underline-offset-[2.5px]">
                Case reference "{{ lastSearchCaseName }}" already exists, please click here to view the case.
              </span>
            </a>
          </DialogDescription>
          <DialogDescription
            v-else-if="state === 'NOT_EXISTS'"
            class="tw-flex tw-select-none tw-items-center tw-gap-2 tw-rounded-md tw-border tw-bg-green-200/45 tw-p-2 tw-text-green-900"
          >
            <i class="fas fa-sm fa-spin fa-spinner"></i>
            Redirecting to case "{{ lastSearchCaseName }}"
          </DialogDescription>
          <DialogDescription
            v-else-if="state === 'FORBIDDEN'"
            class="tw-flex tw-select-none tw-items-center tw-gap-2 tw-rounded-md tw-border tw-bg-red-200/45 tw-p-2 tw-text-red-900"
          >
            <i class="fas fa-triangle-exclamation"></i>
            Case reference "{{ lastSearchCaseName }}" already exists, but is forbidden and cannot be viewed.
          </DialogDescription>
          <div class="max-w-md tw-relative tw-shrink-0">
            <label for="caseName" class="tw-block tw-p-1 tw-pl-0 tw-text-sm tw-font-medium">Case Reference</label>
            <div class="tw-relative tw-w-full">
              <input
                id="caseName"
                v-model.trim="currentCaseName"
                aria-label="Name of case to create"
                class="tw-w-full tw-rounded-md tw-border tw-border-gray-500 tw-p-2 tw-pr-8"
                :required="true"
                :readonly="isLoading"
                :minlength="CASE_REFERENCE_LENGTH_MIN"
                :maxlength="CASE_REFERENCE_LENGTH_MAX"
                :class="{
                  'tw-pointer-events-none tw-select-none tw-bg-gray-200 tw-text-gray-600': isLoading,
                  'tw-bg-white': !isLoading,
                }"
              />
              <span
                class="tw-absolute tw-right-1.5 tw-top-1/2 -tw-translate-y-1/2 tw-select-none tw-text-xs"
                :class="{ 'tw-font-medium tw-text-red-700': charsRemaining < CASE_REFERENCE_LENGTH_THRESHOLD }"
              >
                {{ charsRemaining }}
              </span>
            </div>
          </div>
          <div v-if="errorReason != null" class="tw-text-xs tw-text-red-700">
            {{ getCaseReferenceValidationMessage(errorReason) }}
          </div>
          <div class="tw-flex tw-items-center tw-justify-end tw-gap-2">
            <DialogClose class="tw-btn-md-gray tw-min-w-20">Cancel</DialogClose>
            <button type="submit" :disabled="isLoading" class="tw-btn-md-green tw-min-w-20">
              <i v-if="isLoading" class="fas fa-spinner fa-sm fa-spin"></i>
              Create
            </button>
          </div>
        </form>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
